import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { BellIcon, EmailIcon, GoalIcon, PlusIcon, SendIcon, UploadIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { sendMissionTrackingContracts } from '../../api/mission-trackings/send-contracts'
import {
  sendEmailToMissionTrackings,
  sendMissionFinalContractReminderToStaffs,
  sendMissionFinalMissingDocumentsReminderToStaffs,
  sendMissionFriendlyContractReminderToStaffs,
  sendMissionFriendlyMissingDocumentsReminderToStaffs,
  sendMissionReportReminderToStaffs
} from '../../api/mission-trackings/send-email'
import { uploadDpaeAcknowledgmentReceipt } from '../../api/mission-trackings/uplaod-dpae-acknowledgment-receipt'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import DpaeFormModal from '../../components/pages/dpae-modal/dpae-form-modal'
import { MissionTrackingFilter } from '../../components/pages/mission-trackings/mission-tracking-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import {
  MissionTracking,
  MissionTrackingFilters
} from '../../lib/types/models/mission-tracking/mission-tracking'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformMissionTrackingFiltersToUrl,
  transformUrlToMissionTrackingFilters
} from '../../lib/utils/filters-mapping/mission-tracking'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { missionTrackingColumns } from './utils'

export function MissionTrackingListPage() {
  const { navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const tableRef = React.useRef<DynamicTableRef<MissionTracking>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'send-email' | 'upload-dpae-acknowledgment-receipt',
    MissionTracking[]
  >(['filters', 'send-email', 'upload-dpae-acknowledgment-receipt'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<MissionTrackingFilters>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformMissionTrackingFiltersToUrl,
      transformUrlToFilter: transformUrlToMissionTrackingFilters
    })

  const sendEmail = useMutation({
    mutationFn: sendEmailToMissionTrackings,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const sendContracts = useMutation({
    mutationFn: sendMissionTrackingContracts,
    onSuccess: () => toast.success('Les contrats ont été envoyés avec succès'),
    onError: showAxiosError
  })

  const { mutateAsync: sendMissionFriendlyContractReminderToStaffsMutation } = useMutation({
    mutationFn: sendMissionFriendlyContractReminderToStaffs,
    onError: showAxiosError
  })

  const { mutateAsync: sendMissionFinalContractReminderToStaffsMutation } = useMutation({
    mutationFn: sendMissionFinalContractReminderToStaffs,
    onError: showAxiosError
  })

  const { mutateAsync: sendMissionFriendlyMissingDocumentsReminderToStaffsMutation } = useMutation(
    {
      mutationFn: sendMissionFriendlyMissingDocumentsReminderToStaffs,
      onError: showAxiosError
    }
  )

  const { mutateAsync: sendMissionFinalMissingDocumentsReminderToStaffsMutation } = useMutation({
    mutationFn: sendMissionFinalMissingDocumentsReminderToStaffs,
    onError: showAxiosError
  })

  const { mutateAsync: sendMissionReportReminderToStaffsMutation } = useMutation({
    mutationFn: sendMissionReportReminderToStaffs,
    onError: showAxiosError
  })

  const uploadDpae = useMutation({
    mutationFn: uploadDpaeAcknowledgmentReceipt,
    onSuccess: () => {
      toast.success('Le document a été mis à jour avec succès')
      closeModel('upload-dpae-acknowledgment-receipt')
    },
    onError: showAxiosError
  })

  async function downloadDpaeXml(mts: MissionTracking[]) {
    const ids = mts.map((mt) => mt.id)
    await downloadDocuments({ ids, endpoint: API.missionTrackings.exportDpaeXml.url })
    tableRef.current?.refetch()
  }

  // Shared helpers to DRY validation + mutation flows
  const remindContracts = async (missionTrackings: MissionTracking[], isFinal = false) => {
    const ids = missionTrackings.map((mt) => mt.id)
    if (isFinal) await sendMissionFinalContractReminderToStaffsMutation({ ids })
    else await sendMissionFriendlyContractReminderToStaffsMutation({ ids })
    toast.success('Les relances ont été envoyées aux personnes concernées.')
  }

  const remindMissingDocuments = async (missionTrackings: MissionTracking[], isFinal = false) => {
    const ids = missionTrackings.map((mt) => mt.id)
    if (isFinal) await sendMissionFinalMissingDocumentsReminderToStaffsMutation({ ids })
    else await sendMissionFriendlyMissingDocumentsReminderToStaffsMutation({ ids })
    toast.success('Les relances ont été envoyées aux personnes concernées.')
  }

  return (
    <div>
      <UserNavBar title={'Suivi administratif des AAT'} icon={GoalIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<MissionTracking, MissionTrackingFilters>
        title={'Tous les suivis'}
        columns={missionTrackingColumns}
        endpoint={API.missionTrackings.getAll.url}
        selectable
        ref={tableRef}
        allowSearchParam
        onLoadingChange={setIsTableLoading}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        onRowClick={(mt, event) =>
          navigateWithEvent(
            Paths.MISSION_TRACKING_UPDATE_PAGE.replace(':id', mt.id.toString()),
            event
          )
        }
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            icon: EmailIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Email groupé',
            fn: (mts) => {
              const filteredMts = mts.filter(
                (mt, index, self) =>
                  mt.staff.user.email &&
                  index === self.findIndex((t) => t.staff.user.email === mt.staff.user.email)
              )
              toggleModal('send-email', filteredMts)
            }
          },
          {
            type: 'multiple',
            icon: SendIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Envoyer les contrats',
            fn: (mts) => sendContracts.mutate({ ids: mts.map((mt) => mt.id) })
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Relance contrat',
            variant: 'ghost',
            refetch: true,
            fn: async (missionTrackings) => remindContracts(missionTrackings)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Derniere relance contrat',
            variant: 'ghost',
            refetch: true,
            fn: async (missionTrackings) => remindContracts(missionTrackings, true)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Relance document(s) manquant(s)',
            variant: 'ghost',
            refetch: true,
            fn: async (missionTrackings) => remindMissingDocuments(missionTrackings)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Derniere relance document(s) manquant(s)',
            variant: 'ghost',
            refetch: true,
            fn: async (missionTrackings) => remindMissingDocuments(missionTrackings, true)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Relance rapport mission',
            variant: 'ghost',
            refetch: true,
            fn: async (missionTrackings) => {
              await sendMissionReportReminderToStaffsMutation({
                ids: missionTrackings.map((mt) => mt.id)
              })
              toast.success('Les emails ont été envoyés avec succès')
            }
          },
          {
            type: 'multiple',
            icon: PlusIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Générer les DPAE',
            fn: downloadDpaeXml
          },
          {
            type: 'global',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Importer AR DPAE',
            fn: (mt) => toggleModal('upload-dpae-acknowledgment-receipt', mt)
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <MissionTrackingFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <>
          {/* Send email to staff */}
          <Modal
            hideActions
            size="medium"
            icon={EmailIcon}
            title={'Envoyer un email'}
            isOpen={modalState['send-email']}
            onCancel={() => closeModel('send-email')}
          >
            <SendEmailForm
              className="modal-body"
              defaultValues={{
                emails: selectedData
                  .filter(
                    (mt, index, self) =>
                      index === self.findIndex((t) => t.staff.user.email === mt.staff.user.email)
                  )
                  .map((row) => row.staff.user.email),
                subject: '',
                body: ''
              }}
              labels={selectedData.map(
                (mt) =>
                  `${StringFormatter.capitalize(mt.staff.firstName)} ${mt.staff.lastName.toUpperCase()} - ${mt.staff.user.email}`
              )}
              buttonsConfig={modalButtonConfig({
                isLoading: sendEmail.isPending,
                onCancel: () => closeModel('send-email'),
                onConfirm: (v) =>
                  sendEmail.mutate({
                    ...v,
                    emails: selectedData.map((mt) => mt.staff.user.email)
                  })
              })}
            />
          </Modal>

          {/* upload dpae acknowledgment receipt */}
          <DpaeFormModal
            isOpen={modalState['upload-dpae-acknowledgment-receipt']}
            buttonsConfig={modalButtonConfig({
              isLoading: uploadDpae.isPending,
              onConfirm: uploadDpae.mutate,
              onCancel: () => closeModel('upload-dpae-acknowledgment-receipt')
            })}
            onCancel={() => closeModel('upload-dpae-acknowledgment-receipt')}
          />
        </>
      )}
    </div>
  )
}
